import { Button, MenuItem, Select, SelectChangeEvent, TextField } from '@mui/material';
import { useFormik } from 'formik';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import * as Yup from 'yup';

import { showError, showToast } from '../../constants/toasts';
import MainContainer from '../../layout/MainContainer';
import {
  useAddPackageMutation,
  useUpdatePackageMutation,
  useLazyGetPackageByIdQuery,
} from '../../service/packages';
import { CommonBody } from '../../types/General';
import { generateEncryptedKeyBody } from '../../utils/crypto';
import { SUBSCRIPTION_PLAN } from '../../utils/enums';
import { isNumber } from '../../utils/validations';

const AddPackage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [validity, setValidity] = useState('default');
  const [addPackage] = useAddPackageMutation();
  const [updatedPackage] = useUpdatePackageMutation();
  const [packageById] = useLazyGetPackageByIdQuery();
  const [loading, setLoading] = useState(false);

  const handleChange = (event: SelectChangeEvent) => {
    formik.setFieldValue('validity', event.target.value as string);
  };

  const formik = useFormik({
    enableReinitialize: true,

    initialValues: {
      name: '',
      price: '',
      validity: 'default',
      noOfListings: '',
    },

    validationSchema: Yup.object({
      name: Yup.string()
        .required('Package title is required')
        .min(2, 'Minimum 2 characters are required')
        .max(30, 'Maximum 30 characters allowed'),

      price: Yup.string()
        .required('Price is required')
        .matches(/^[1-9]\d*$/, 'Price must be a positive whole number'),

      noOfListings: Yup.string()
        .required('No. of listings is required')
        .matches(/^[1-9]\d*$/, 'Must be a positive whole number'),

      validity: Yup.string()
        .notOneOf(['default'], 'Please select a validity')
        .required('Validity is required'),
    }),
    onSubmit: async (values: any) => {
      formik.setSubmitting(true);
      // if (vehicleType === "default") {
      //   return;
      // }
      const body = {
        title: values.name,
        price: values.price,
        validity: values.validity,
        noOfListings: Number(values.noOfListings),
      };

      // return

      if (id) {
        try {
          setLoading(true);
          const encryptedBody = generateEncryptedKeyBody(body) as CommonBody;
          const response = await updatedPackage({
            body: encryptedBody,
            id,
          }).unwrap();
          setLoading(false);

          if (response?.statusCode === 200) {
            showToast('Package updated successfully');
            navigate('/manage-packages');
          }
        } catch (error: any) {
          setLoading(true);

          showError(error?.data?.message || '');
          console.error(error);
        }
      } else {
        try {
          setLoading(true);

          const encryptedBody = generateEncryptedKeyBody(body) as CommonBody;
          const response = await addPackage({
            body: encryptedBody,
          }).unwrap();
          setLoading(false);

          if (response?.statusCode === 200) {
            showToast('Package added successfully');
            navigate('/manage-packages');
          }
        } catch (error: any) {
          setLoading(true);
          showError(error?.data?.message || '');
          console.error(error);
        }
      }
    },
  });

  const getData = async () => {
    setLoading(true);
    try {
      const response = await packageById({
        id: id as string,
      }).unwrap();
      setLoading(false);

      if (response?.statusCode === 200) {
        formik.setFieldValue('name', response?.data?.title);
        formik.setFieldValue('noOfListings', response?.data?.noOfListings);
        formik.setFieldValue('price', response?.data?.price);
        formik.setFieldValue('validity', response?.data?.validity);
        // setVehicleType(response?.data?.vehicle?._id);
      }
    } catch (error: any) {
      showError(error?.data?.message || '');
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) getData();
  }, [id]);

  return (
    <MainContainer>
      <div className="main_title fdx">
        <div>
          <h1>{id ? 'Edit' : 'Add'} Package</h1>
          <p>
            <span onClick={() => navigate('/dashboard')}>Dashboard</span> -{' '}
            <span onClick={() => navigate('/manage-packages')}>Packages</span> -{' '}
            {id ? 'Edit' : 'Add'} Package{' '}
          </p>
        </div>
        <div className="right_s align_right">
          <Button className="btn btn_orange" onClick={() => navigate(-1)}>
            Back
          </Button>
        </div>
      </div>

      <div className="cards">
        <form className="form" action="" onSubmit={formik.handleSubmit}>
          <div className="gap_p">
            <div className="control_group w_50">
              <label>Package Title</label>
              <TextField
                variant="outlined"
                placeholder="Package Title"
                fullWidth
                hiddenLabel
                name="name"
                id="name"
                className="text_field"
                inputProps={{ maxLength: 30 }}
                value={formik.values.name}
                onChange={(val) => {
                  if (val.target.value === ' ' || val.target.value === '.') {
                  } else {
                    formik.handleChange(val);
                  }
                }}
                onBlur={formik.handleBlur}
                helperText={formik.touched.name && formik.errors.name}
              />
            </div>

            <div className="control_group w_50">
              <label>No. of listings allowed</label>
              <TextField
                variant="outlined"
                placeholder="No. of listings allowed"
                fullWidth
                hiddenLabel
                name="noOfListings"
                id="noOfListings"
                className="text_field"
                inputProps={{ maxLength: 30 }}
                value={formik.values.noOfListings}
                onChange={(val) => {
                  if (
                    val.target.value === ' ' ||
                    val.target.value === '.' ||
                    val.target.value === '0' ||
                    !isNumber(val.target.value)
                  ) {
                    return;
                  } else {
                    formik.handleChange(val);
                  }
                }}
                onBlur={formik.handleBlur}
                helperText={formik.touched.noOfListings && formik.errors.noOfListings}
              />
            </div>
            <div className="control_group w_50">
              <label>Package Price</label>
              <TextField
                variant="outlined"
                placeholder="Package Price"
                fullWidth
                hiddenLabel
                name="price"
                id="price"
                className="text_field"
                inputProps={{ maxLength: 30 }}
                value={formik.values.price}
                onChange={(val) => {
                  if (
                    val.target.value === ' ' ||
                    val.target.value === '.' ||
                    val.target.value === '0' ||
                    !isNumber(val.target.value)
                  ) {
                    return;
                  } else {
                    formik.handleChange(val);
                  }
                }}
                onBlur={formik.handleBlur}
                helperText={formik.touched.price && formik.errors.price}
              />
            </div>
            <div className="control_group w_50">
              <label>Validity</label>
              <Select
                fullWidth
                labelId="residencyType"
                id="residencyType"
                value={formik.values.validity}
                onChange={handleChange}
              >
                <MenuItem disabled value="default">
                  Select Validity
                </MenuItem>
                <MenuItem value={SUBSCRIPTION_PLAN.MONTHLY}>1 Month</MenuItem>
                {/* <MenuItem value={20}>3 Months</MenuItem> */}
                {/* <MenuItem value={30}>1 Year</MenuItem> */}
              </Select>
              {(formik.errors.validity || formik.touched.validity) && (
                <h6 className="err_msg">{formik.errors.validity || formik.touched.validity}</h6>
              )}
            </div>
          </div>

          <div className="form_end_btn" style={{ marginTop: '20px' }}>
            <Button
              className="btn btn_secondary"
              type="submit"
              // onClick={() => navigate("/manage-packages")}
            >
              Submit
            </Button>
          </div>
        </form>
      </div>
    </MainContainer>
  );
};

export default AddPackage;
